import {
  Button,
  ButtonGroup,
  Card,
  ConfirmationModal,
  FileDropzone,
  FileInput,
  FileList,
  FileListButton,
  FormButtonGroup,
  FormCol,
  FormRow,
  Heading,
  ModalBody,
  Paragraph,
} from "@george-labs.com/design-system"
import {
  DocumentDTO,
  DocumentTypeTypeEnum,
  GetContentRq,
  OnlineInfoDTO,
  OnlineRectificationFinishedRq,
} from "@smartx/mikro-vt-client-api"
import { saveAs } from "file-saver"
import { useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router"
import { useLoanCase, useLoanCases } from "../../app/hooks"
import {
  resetLabel,
  setActive,
  setLabel,
} from "../../app/slices/snackbarItemSlice"
import { store } from "../../app/store"
import * as fileHandler from "../../functions/fileHandler"
import AuthContext from "../../providers/AuthProvider"
import ConfigContext from "../../providers/ConfigProvider"
import { ModalComponent } from "../../Shared"
import { RequestHeader } from "../../utils/utils"
import {
  fileNetApi,
  fileNetProxyApi,
  rectificationApi,
} from "../../api/apiClient"
import { StoreMultipartFileDocumentTypeEnum } from "@smartx/mikro-vt-client-api-proxy"

interface ITempArr {
  name: string
  objectId: string
  type: string
}

const getOnlineRectificationRq = (
  onlineInfo: OnlineInfoDTO,
): OnlineRectificationFinishedRq => {
  const data: OnlineRectificationFinishedRq = {
    header: new RequestHeader(),
    caseId:
      onlineInfo?.onlineRectificationLevel === "CASE"
        ? onlineInfo.caseId
        : null,
    caseEventCode:
      onlineInfo?.onlineRectificationLevel === "EVENT"
        ? onlineInfo.eventCode
        : null,
  }

  return data
}

const Rectification = () => {
  const authContext = useContext(AuthContext)
  const configContext = useContext(ConfigContext)

  const navigate = useNavigate()
  const { t } = useTranslation()

  const { caseId } = useParams<{ caseId: string }>()
  const [modalOpen, setModalOpen] = useState(false)
  const [uploadedFiles, setUploadedFiles] = useState<any>([])
  const [errorType, setErrorType] = useState("")
  const [isSuccess, setIsSuccess] = useState(false)
  const [error, setError] = useState(false)
  const [beError, setBeError] = useState("")

  const cases = useLoanCases()
  const onlineInfo = useLoanCase(caseId)?.loanCaseStatus?.onlineInfo

  const maxFileSize = configContext.maxFileSizeMb * 1024 * 1024
  const onlineRectificationDocType: DocumentTypeTypeEnum = "ONLINE_HIANYPOTLAS"

  useEffect(() => {
    authContext.refresh()

    const caseDocuments = cases?.find(
      (item) => item.caseId === caseId,
    )?.documents
    fillFileArr(caseDocuments || [])
  }, [caseId, cases, authContext])

  const fillFileArr = (arr: Array<DocumentDTO>) => {
    let tempArr: Array<ITempArr> = []
    arr.forEach((document) => {
      tempArr.push({
        name: document.fileName,
        objectId: document.filenetId,
        type: document.type.type,
      })
    })
    setUploadedFiles(
      tempArr.filter((document) => document.type === "ONLINE_HIANYPOTLAS"),
    )
  }

  const getOnlineRectificationDocType = () => {
    return onlineRectificationDocType
  }

  const renderDownloadButton = (file: any) => (
    <FileListButton
      icon={FileListButton.ICON.FILE_DOWNLOAD}
      onClick={() => downloadFile(file)}
    >
      {t("documents.download.aria")}
    </FileListButton>
  )

  const fileAttached = async (file: any) => {
    store.dispatch(setLabel(t("documents.snackbar.upload")))
    store.dispatch(setActive(true))

    if (file && file[0]?.size! > maxFileSize) {
      store.dispatch(setActive(false))
      store.dispatch(resetLabel())
      setErrorType("SIZE_ERROR")
      setError(true)
      return
    }

    if (file && !fileHandler.getAllowedMimeTypes().includes(file[0]?.type)) {
      store.dispatch(setActive(false))
      store.dispatch(resetLabel())
      setErrorType("TYPE_ERROR")
      setError(true)
      return
    }

    const formData = new FormData()
    formData.append("file", file[0])
    formData.append(
      "additionalData",
      JSON.stringify({
        caseId:
          onlineInfo?.onlineRectificationLevel === "CASE"
            ? onlineInfo.caseId
            : "",
        loanCaseEventId:
          onlineInfo?.onlineRectificationLevel === "EVENT"
            ? onlineInfo.eventCode
            : "",
      }),
    )
    formData.append(
      "data",
      JSON.stringify({
        header: { id: "", businessFlowId: [""], sentAt: new Date() },
        metadataList: [],
      }),
    )
    formData.append("documentType", getOnlineRectificationDocType())

    try {
      const res = await fileNetProxyApi.storeMultipartFile(
        formData.get("additionalData") as string,
        formData.get("documentType") as StoreMultipartFileDocumentTypeEnum,
        formData.get("file") as File,
        formData.get("data") as string,
      )

      store.dispatch(setActive(false))
      store.dispatch(resetLabel())
      setError(false)
      setUploadedFiles([
        ...uploadedFiles,
        {
          name: file[0].name,
          id: file[0].id,
          objectId: res.data.objectId,
        },
      ])
    } catch {
      store.dispatch(setActive(false))
      store.dispatch(resetLabel())
      setError(true)
      setErrorType("UPLOAD_ERROR")
    }
  }

  const downloadFile = async (item: any) => {
    store.dispatch(setActive(true))

    const file = fileHandler.getFileById(item.objectId, uploadedFiles)
    const data: GetContentRq = {
      header: new RequestHeader(),
      objectId: file.objectId,
      fileNetStorage: file.fileNetStorage,
      loanCaseEventId: file.loanCaseEventId,
      caseId: caseId,
    }

    try {
      const res = await fileNetApi.getContent(data)
      store.dispatch(setActive(false))
      const blobFile = fileHandler.base64toBlob(
        res.data.content,
        res.data.mimeType,
      )
      saveAs(blobFile, file.name)
    } catch {
      store.dispatch(setActive(false))
      setErrorType("")
      setError(true)
    }
  }

  const onUpload = async () => {
    setModalOpen(false)
    try {
      await rectificationApi.onlineRectificationFinished(
        getOnlineRectificationRq(onlineInfo),
      )
      setIsSuccess(true)
    } catch (err: any) {
      setBeError(err?.error?.data?.header?.resultDetails?.resultMessage || "")
    }
  }

  const getErrorModal = (errorType: string) => {
    let modalText
    let title

    switch (errorType) {
      case "UPLOAD_ERROR":
        modalText = t("documents.file-upload.error.message")
        break
      case "TYPE_ERROR":
        modalText = `Nem támogatott fájlformátum. Csak ${fileHandler
          .getExtensionsByMimeType(fileHandler.getAllowedMimeTypes())
          .join(", ")} típusú állomány tölthető fel!`
        break
      case "SIZE_ERROR":
        modalText = `Túl nagy méretű a feltöltendő állomány. A maximális méret ${configContext.maxFileSizeMb} MB.  Kérjük,  csökkentse  a  fájlméretet  és  töltse  fel  újra!`
        break
      default:
        modalText = t("documents.file-upload.error.default")
        title = t("common.required.title")
        break
    }

    return (
      <ConfirmationModal
        onClose={() => setError(false)}
        animate={true}
        isOpen={error}
        closeLabel="Bezárás"
        confirmationType={ConfirmationModal.CONFIRMATION_TYPE.ERROR}
        title={title || "Feltöltés"}
        footer={
          <ButtonGroup>
            <Button onClick={() => setError(false)} disabled={false}>
              {t("common.button.ok")}
            </Button>
          </ButtonGroup>
        }
      >
        <ModalBody>
          <Paragraph>{modalText}</Paragraph>
        </ModalBody>
      </ConfirmationModal>
    )
  }

  return (
    <>
      <Card className="p-2">
        <FormRow>
          <FormCol md={12}>
            <Heading level={3}>{t("rectification.title")}</Heading>
            <Paragraph>{t("rectification.uploader.subtitle")}</Paragraph>
            <FileInput
              className="mb-2"
              onFileAttach={(file: any) => fileAttached(file)}
              onError={() => setError(true)}
              dropzone={
                <FileDropzone
                  infoText=""
                  draggingText={t("dropzone.text")}
                  browseText={t("dropzone.browseText")}
                  browseButtonText={t("dropzone.browseButtonText")}
                ></FileDropzone>
              }
            ></FileInput>
            <Heading level={3}>{t("rectification.uploader.uploaded")}</Heading>
            <FileList
              files={uploadedFiles}
              showRemoveButton={false}
              renderExtraButtons={renderDownloadButton}
            ></FileList>
          </FormCol>
        </FormRow>
      </Card>
      <FormButtonGroup
        className="text-center"
        style={{ marginBottom: "3.5rem" }}
      >
        <Button
          variant={Button.VARIANT.SECONDARY}
          onClick={() => navigate("/dashboard")}
        >
          {t("common.button.backToDashboard")}
        </Button>
        <Button
          variant={Button.VARIANT.PRIMARY}
          onClick={() => setModalOpen(true)}
        >
          {t("common.button.rectificationDone")}
        </Button>
      </FormButtonGroup>

      {modalOpen && (
        <ModalComponent
          type="info"
          title={t("common.required.title")}
          text={t("rectification.finish.message")}
          onClick={onUpload}
          onClose={() => setModalOpen(false)}
          cancelButtonText={t("common.no")}
          confirmButtonText={t("common.yes")}
        />
      )}

      {isSuccess && (
        <ModalComponent
          type="success"
          title={t("rectification.afterFinish.title")}
          text={t("rectification.afterFinish.message")}
          onClick={() => navigate("/dashboard")}
          onClose={() => {
            setIsSuccess(false)
            navigate("/dashboard")
          }}
          disableCancel={true}
        />
      )}

      {getErrorModal(errorType)}

      {!!beError && (
        <ModalComponent
          type="error"
          title={t("common.warning.title")}
          text={beError}
          onClick={() => setBeError("")}
          onClose={() => setBeError("")}
          disableCancel={true}
        />
      )}
    </>
  )
}

export default Rectification
